import { Button, Card, Form, Input, Select, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Row = {
  id: string;
  screening_no: string;
  screened_on: string;
  domain: string;
  outcome: string;
  summary: string;
  status: string;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
};

export default function InclusionScreeningListPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.inclusion.manage') || user?.permissions?.includes('education.admin')
  );
  const [status, setStatus] = useState<string | undefined>();
  const [domain, setDomain] = useState<string | undefined>();
  const [outcome, setOutcome] = useState<string | undefined>();
  const [q, setQ] = useState('');

  const list = useQuery({
    queryKey: ['education-inclusion-screenings', status, domain, outcome, q],
    queryFn: async () => {
      const res = await api.get<Row[]>('/education/inclusion/screenings', {
        params: { status, domain, outcome, q: q || undefined },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  return (
    <EducationPageChrome
      title={t('inclusionScreenings')}
      description={t('inclusionScreeningsIntro')}
      extra={
        <Space>
          <Button onClick={() => navigate('/education/inclusion')}>{t('inclusion')}</Button>
          {canManage ? (
            <Button type="primary" onClick={() => navigate('/education/inclusion/screenings/new')}>
              {t('newScreening')}
            </Button>
          ) : null}
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline" onFinish={() => list.refetch()}>
            <Form.Item label={t('status')}>
              <Select
                allowClear
                style={{ minWidth: 140 }}
                value={status}
                onChange={(v) => setStatus(v || undefined)}
                options={[
                  { value: 'open', label: t('statusOpen') },
                  { value: 'closed', label: t('statusClosed') },
                  { value: 'cancelled', label: t('statusCancelled') },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('screeningDomain')}>
              <Select
                allowClear
                style={{ minWidth: 160 }}
                value={domain}
                onChange={(v) => setDomain(v || undefined)}
                options={[
                  { value: 'attention', label: t('screeningDomainAttention') },
                  { value: 'learning', label: t('screeningDomainLearning') },
                  { value: 'social_emotional', label: t('screeningDomainSocial') },
                  { value: 'behaviour_observation', label: t('screeningDomainBehaviour') },
                  { value: 'speech_language', label: t('screeningDomainSpeech') },
                  { value: 'motor', label: t('screeningDomainMotor') },
                  { value: 'other', label: t('screeningDomainOther') },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('screeningOutcome')}>
              <Select
                allowClear
                style={{ minWidth: 160 }}
                value={outcome}
                onChange={(v) => setOutcome(v || undefined)}
                options={[
                  { value: 'no_concern', label: t('screeningOutcomeNone') },
                  { value: 'monitor', label: t('screeningOutcomeMonitor') },
                  { value: 'refer_for_support', label: t('screeningOutcomeRefer') },
                  { value: 'consent_pending', label: t('screeningOutcomeConsent') },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('search')}>
              <Input value={q} onChange={(e) => setQ(e.target.value)} allowClear style={{ width: 160 }} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={list.isFetching}>
              {t('filter')}
            </Button>
          </Form>
        </Card>
        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            dataSource={list.data ?? []}
            pagination={{ pageSize: 25 }}
            onRow={(row) => ({
              onClick: () => navigate(`/education/inclusion/screenings/${row.id}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: t('screeningNo'), dataIndex: 'screening_no', width: 150 },
              { title: t('screenedOn'), dataIndex: 'screened_on', width: 120 },
              {
                title: t('student'),
                render: (_: unknown, row: Row) =>
                  `${row.admission_no || ''} — ${row.first_name || ''} ${row.last_name || ''}`.trim(),
              },
              { title: t('screeningDomain'), dataIndex: 'domain', width: 140 },
              { title: t('screeningOutcome'), dataIndex: 'outcome', width: 140 },
              { title: t('summary'), dataIndex: 'summary', ellipsis: true },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 100,
                render: (v: string) => <Tag>{v}</Tag>,
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
